import { useEffect, useState } from "react";
import { Box, Card, LinearProgress, Skeleton, Typography } from "@mui/material";
import axiosInstance from "../../axiosInstance";
import { agencyBadgeImage } from "./AgencyBadgeChip";

/**
 * Agency Badge card (feature: agency_commission_badges).
 *
 * Shown on the partner profile: badge medallion, commission band, students
 * booked this academic year, and how many more students reach the next badge.
 *
 * The endpoint always 200s; `feature_enabled: false` means render nothing —
 * the feature is off, or this partner is not an agency.
 */
export default function AgencyBadgeCard() {
  const [badge, setBadge] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const accessToken = localStorage.getItem("accessToken");
        const token = localStorage.getItem("loginUser");

        const response = await axiosInstance.get(
          `${process.env.REACT_APP_BASE_URL}dashboard/agency_badge`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              XRefreshToken: `${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!cancelled) setBadge(response?.data ?? null);
      } catch (error) {
        // Never block the profile page on the badge card.
        if (!cancelled) setBadge(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Card sx={{ borderRadius: "1rem", padding: "1.5rem" }}>
        <Skeleton variant="text" width={120} height={28} />
        <Skeleton variant="rounded" height={44} sx={{ marginTop: "0.75rem" }} />
        <Skeleton variant="text" width="60%" sx={{ marginTop: "0.75rem" }} />
      </Card>
    );
  }

  if (!badge || !badge.feature_enabled) return null;

  if (!badge.current_badge) {
    return (
      <Card sx={{ borderRadius: "1rem", padding: "1.5rem" }}>
        <Typography sx={{ fontWeight: 600, marginBottom: "0.5rem" }}>
          Agency Badge
        </Typography>
        <Typography sx={{ fontSize: "14px", opacity: 0.7 }}>
          No badge yet — your first booking of this academic year starts you on{" "}
          {badge.next_badge || "the first badge"}.
        </Typography>
      </Card>
    );
  }

  const image = agencyBadgeImage(badge.current_badge);
  const progress = Math.min(100, Math.max(0, badge.progress_percentage ?? 0));
  const students = badge.students_booked ?? 0;
  const toNext = badge.students_to_next_badge ?? 0;

  return (
    <Card
      sx={{
        borderRadius: "1rem",
        padding: "1.5rem",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "1.25rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          {image && (
            <img
              src={image}
              alt={`${badge.current_badge} agency badge`}
              width="56px"
              height="56px"
              style={{
                borderRadius: "50%",
                boxShadow: "0 0 4px rgba(0,0,0,0.25)",
                flexShrink: 0,
              }}
            />
          )}
          <Box>
            <Typography sx={{ fontSize: "13px", opacity: 0.65 }}>
              {/* window: the counting period the standing is derived from —
                  a calendar year, or "since <month>" when counts never reset. */}
              Agency Badge{badge.window ? ` · ${badge.window}` : ""}
            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                textTransform: "capitalize",
                marginTop: "0.2rem",
              }}
            >
              {badge.current_badge}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontSize: "28px", fontWeight: 700, lineHeight: 1 }}>
            {students}
          </Typography>
          <Typography sx={{ fontSize: "13px", opacity: 0.65 }}>
            {students === 1 ? "student booked" : "students booked"}
          </Typography>
        </Box>
      </Box>

      <Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 8, borderRadius: 999 }}
        />
        <Typography sx={{ fontSize: "13px", opacity: 0.75, marginTop: "0.6rem" }}>
          {badge.is_max_level
            ? "Top badge reached — you are on the highest commission rate."
            : `${toNext} more ${toNext === 1 ? "student" : "students"} to reach ${
                badge.next_badge
              }`}
        </Typography>
      </Box>
    </Card>
  );
}
